import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  TrendingUp,
  TrendingDown,
  LayoutGrid,
  Calendar,
  History,
  Activity,
  ChevronRight,
  ChevronLeft,
  X,
  Edit2,
  Camera,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  List,
  Link,
  Image as ImageIcon,
  Table,
  RefreshCw,
  AlertCircle,
  Copy,
  Check,
  Loader2,
  FileDown
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { useCexAccount, ALL_ACCOUNTS_ID } from '../context/CexAccountContext';
import { useTranslation } from 'react-i18next';
import { usePortfolio } from '../context/PortfolioContext';
import { apiService } from '../services/apiService';
import { ProductTour, TourStep } from '../components/ProductTour';
import { useSEO } from '../hooks/useSEO';
import { API_BASE_URL } from '../config/api';
import { useSubscription } from '../context/SubscriptionContext';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

interface ReportsPageProps {
  formData: any;
}

const getDynamicFontSizeStyle = (val: string, baseSizeMobile = 20, baseSizeDesktop = 30) => {
  if (!val || typeof val !== 'string') return {};
  const len = val.length;
  if (len <= 7) return {};
  const reduction = (len - 7) * 0.85;
  const sizeMobile = Math.max(12, baseSizeMobile - reduction);
  const sizeDesktop = Math.max(14, baseSizeDesktop - reduction);
  return {
    fontSize: `clamp(${sizeMobile}px, 2vw, ${sizeDesktop}px)`
  };
};

const LOGO_BASE64 = "data:image/png;base64,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";

const ReportsPage: React.FC<ReportsPageProps> = ({ formData }) => {
  useSEO({
    title: 'Performance Reports & Analytics',
    description: 'Analisis performa mendalam CatatCrypto. Dapatkan data win rate harian, ROI, grafik pertumbuhan, statistik koin terbaik, dan laporan harian.',
    keywords: 'reports, trading performance, ROI tracking, daily PnL, coin statistics'
  });

  const { portfolioData, setPortfolioData, lastSyncedAccountId, setLastSyncedAccountId } = usePortfolio();
  const { selectedAccount, selectedWalletAccount } = useCexAccount();
  const { t } = useTranslation();
  const { effectivePlan } = useSubscription();
  const [activeTab, setActiveTab] = useState('Overview');
  const [selectedDate, setSelectedDate] = useState<any | null>(null);
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [dataErrors, setDataErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const isFirstMount = React.useRef(true);
  const [invertedLogoB64, setInvertedLogoB64] = React.useState<string>('');

  // Pre-compute pixel-inverted version of the logo for use in PDF export.
  // html2canvas does NOT support CSS filter, so we must invert the pixels
  // directly — otherwise white logo on white PDF = invisible watermark.
  React.useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, c.width, c.height);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] > 10) { // only invert non-transparent pixels
          d[i]     = 255 - d[i];       // R
          d[i + 1] = 255 - d[i + 1];   // G
          d[i + 2] = 255 - d[i + 2];   // B
        }
      }
      ctx.putImageData(imageData, 0, 0);
      setInvertedLogoB64(c.toDataURL('image/png'));
    };
    img.src = LOGO_BASE64;
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: 'body',
      title: "Welcome to the Analysis & Reports Page!",
      content: "This page provides in-depth trading performance statistics from all your exchange accounts in real-time.",
      position: 'center'
    },
    {
      target: '[data-tour="reports-tabs"]',
      title: "Choose Analysis Display Mode",
      content: "Choose the Overview tab for general performance, the Calendar tab for daily calendar PnL visualization, and the History tab for a complete performance audit history.",
      position: 'bottom'
    },
    {
      target: '[data-tour="reports-demo-toggle"]',
      title: "Demo Simulator & Live Data",
      content: "Switch to Demo mode to view premium simulated data, or use Live to load your real trading performance instantly.",
      position: 'bottom'
    },
    {
      target: '[data-tour="reports-balance"]',
      title: "Your Asset Growth Chart",
      content: "Displays your cumulative portfolio balance along with a daily profitability histogram for the last 10 days.",
      position: 'right'
    },
    {
      target: '[data-tour="reports-stats"]',
      title: "Key Trading Metrics",
      content: "Monitor the average profit of winning positions (Winners) vs average loss of losing positions (Losers), Long vs Short performance, and the record for largest trade profit/loss.",
      position: 'left'
    }
  ];

  // Demo Mode States
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [hasSetDefaultDemo, setHasSetDefaultDemo] = useState(false);

  // Trades fetched from DB
  const [trades, setTrades] = useState<any[]>([]);
  const [isLoadingTrades, setIsLoadingTrades] = useState(false);

  // Hold Time Analysis
  const [holdTimeData, setHoldTimeData] = useState<any>(null);
  const [isLoadingHoldTime, setIsLoadingHoldTime] = useState(false);

  // Journal state
  const [journalRemarks, setJournalRemarks] = useState('');
  const [journalScreenshots, setJournalScreenshots] = useState<string[]>([]);
  const [isSavingJournal, setIsSavingJournal] = useState(false);
  const [journalSaveError, setJournalSaveError] = useState('');
  const [journalSaveSuccess, setJournalSaveSuccess] = useState(false);
  const [copiedScreenshotIdx, setCopiedScreenshotIdx] = useState<number | null>(null);

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  // Fetch trades from DB
  const fetchTrades = async () => {
    setIsLoadingTrades(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const params = new URLSearchParams();
      if (selectedWalletAccount) {
        params.append('wallet_account_id', selectedWalletAccount.id.toString());
      } else if (selectedAccount && selectedAccount.id !== ALL_ACCOUNTS_ID) {
        params.append('cex_account_id', selectedAccount.id.toString());
      }

      const response = await fetch(`${API_BASE_URL}/api/trades?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTrades(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch trades in Reports:', error);
    } finally {
      setIsLoadingTrades(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [selectedAccount, selectedWalletAccount]);

  // Auto-sync or auto-toggle demo mode based on synced portfolio data
  useEffect(() => {
    if (portfolioData && !hasSetDefaultDemo) {
      setIsDemoMode(false);
      setHasSetDefaultDemo(true);
    } else if (!portfolioData && !hasSetDefaultDemo) {
      setIsDemoMode(true);
    }
  }, [portfolioData, hasSetDefaultDemo]);

  // Fetch Hold Time Analysis when not in demo mode
  useEffect(() => {
    if (!isDemoMode && formData.token) {
      const fetchHoldTime = async () => {
        setIsLoadingHoldTime(true);
        try {
          const response = await apiService.getHoldTimeAnalysis(formData.token);
          if (response.success) {
            setHoldTimeData(response.data);
          }
        } catch (error) {
          console.error('Failed to fetch hold time analysis:', error);
        } finally {
          setIsLoadingHoldTime(false);
        }
      };
      fetchHoldTime();
    }
  }, [isDemoMode, formData.token, portfolioData]);

  // Validate portfolio data and set errors
  const validatePortfolioData = (data: any) => {
    const errors: Record<string, string> = {};

    if (!data) return;

    // 1. Account Balance
    if (!data.totalBalance || data.totalBalance === "$0.00") {
      errors.accountBalance = "Saldo tidak tersedia";
    }

    // 2. Return on Winners/Losers
    if (!data.avgWinLoss || data.avgWinLoss === "$0 / $0") {
      errors.returnOnWinners = "Belum ada data trading untuk dihitung";
    }

    // 3. Return on Long/Short
    if (!data.returnOnLong || !data.returnOnShort || (data.returnOnLong === "+$0.00" && data.returnOnShort === "+$0.00")) {
      errors.returnOnLongShort = "Belum ada data trading untuk dihitung";
    }

    // 4. Biggest Profit/Loss
    if (!data.performance || data.performance.length === 0) {
      errors.biggestProfit = "Belum ada riwayat trading";
    }

    // 5. Highcap PNL
    if (!data.highcapPnl || data.highcapPnl === "$0.00") {
      errors.highcapPnl = "Belum ada riwayat trading";
    }

    // 6. Midcap/Lowcap PNL
    if (!data.midcapPnl || data.midcapPnl === "$0.00") {
      errors.midcapPnl = "Belum ada data trading untuk dihitung";
    }
    if (!data.lowcapPnl || data.lowcapPnl === "$0.00") {
      errors.lowcapPnl = "Belum ada data trading untuk dihitung";
    }

    // 7. Profit by Day Chart
    if (!data.performance || data.performance.length < 7) {
      errors.profitByDay = "Belum ada data trading untuk dihitung";
    }

    // 8. Best Primary Setup
    if (!data.netPnl || data.netPnl === "$0.00") {
      errors.bestSetup = "Belum ada riwayat trading";
    }

    // 9. Best Indicator
    if (!data.tradeWinRate || data.tradeWinRate === "0%") {
      errors.bestIndicator = "Belum ada data trading untuk dihitung";
    }

    // 10. Hold Time vs PnL
    if (!data.performance || data.performance.length === 0) {
      errors.holdTime = "Belum ada riwayat trading";
    }

    // 11. Performance Calendar
    if (!data.performance || data.performance.length === 0) {
      errors.calendar = "API terhubung tapi belum ada riwayat trading bulan ini";
    }

    setDataErrors(errors);
  };

  // Validate on data change
  useEffect(() => {
    if (portfolioData) {
      validatePortfolioData(portfolioData);
    } else {
      setDataErrors({});
    }
  }, [portfolioData]);

  const handleResync = async (showToast: boolean = true, fastMode: boolean = false) => {
    setIsSyncing(true);
    setSyncError('');

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setSyncError('Authentication required. Please login again.');
        return;
      }

      let url = '';
      if (selectedWalletAccount) {
        url = `${API_BASE_URL}/api/wallet-accounts/${selectedWalletAccount.id}/sync`;
      } else {
        const isAllAccounts = !selectedAccount || selectedAccount.id === ALL_ACCOUNTS_ID;
        url = isAllAccounts
          ? `${API_BASE_URL}/api/cex-accounts/sync-all`
          : `${API_BASE_URL}/api/cex-accounts/${selectedAccount.id}/sync`;
      }

      if (fastMode) {
        url += (url.includes('?') ? '&' : '?') + 'fast_mode=1';
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to sync account');
      }

      const data = await response.json();
      if (data.success) {
        setPortfolioData(data.data);
        if (selectedWalletAccount) {
          setLastSyncedAccountId(`wallet-${selectedWalletAccount.id}`);
        } else {
          const currentCexId = selectedAccount ? selectedAccount.id : ALL_ACCOUNTS_ID;
          if (currentCexId === ALL_ACCOUNTS_ID) {
            setLastSyncedAccountId('cex-all');
          } else {
            setLastSyncedAccountId(`cex-${currentCexId}`);
          }
        }
        setSyncError('');
      } else {
        throw new Error(data.message || 'Sync failed');
      }
    } catch (e: any) {
      if (selectedAccount || selectedWalletAccount) {
        setSyncError(e.message || 'Failed to sync. Please check your credentials/address.');
      } else {
        console.warn('Failed to sync manual trades:', e);
      }
    } finally {
      setIsSyncing(false);
    }
  };

  // Auto-sync logic (shared with DashboardPage)
  useEffect(() => {
    if (!isSyncing) {
      if (effectivePlan === 'expired') {
        return;
      }

      let selectedAccountId = 'cex-all';
      if (selectedWalletAccount) {
        selectedAccountId = `wallet-${selectedWalletAccount.id}`;
      } else if (selectedAccount) {
        selectedAccountId = selectedAccount.id === ALL_ACCOUNTS_ID ? 'cex-all' : `cex-${selectedAccount.id}`;
      }

      // Skip sync if we already have data for THIS exact account
      if (portfolioData && lastSyncedAccountId === selectedAccountId) {
        console.log('✅ ReportsPage: Using cached data for account ID:', selectedAccountId);
        validatePortfolioData(portfolioData);
        isFirstMount.current = false;
        return;
      }

      // First mount: Sync only if no data OR data is for different account
      if (isFirstMount.current) {
        isFirstMount.current = false;

        if (portfolioData && lastSyncedAccountId === selectedAccountId) {
          console.log('✅ ReportsPage: Found valid data on first mount, skipping sync');
          return;
        }

        console.log('🔄 ReportsPage: Initial auto-sync for account ID:', selectedAccountId);
        handleResync(false);
        return;
      }

      // Subsequent changes (manual account switch): Use fast/local view if account changed, don't force a live API call!
      if (selectedAccountId !== lastSyncedAccountId) {
        console.log('🔄 ReportsPage: Account changed, loading local cached data for ID:', selectedAccountId);
        handleResync(false, true);
      }
    }
  }, [selectedAccount?.id, selectedWalletAccount?.id, lastSyncedAccountId]);

  const handleSaveJournal = async () => {
    if (!selectedDate || !formData.token) {
      setJournalSaveError('Unable to save. Please login again.');
      return;
    }

    setIsSavingJournal(true);
    setJournalSaveError('');
    setJournalSaveSuccess(false);

    try {
      if (isDemoMode) {
        // Just simulate a successful save in Demo Mode
        setJournalSaveSuccess(true);
        setIsEditingDate(false);
        setTimeout(() => setJournalSaveSuccess(false), 3000);
        return;
      }

      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate).padStart(2, '0');
      const tradeDate = `${year}-${month}-${day}`;

      const accId = selectedAccount && selectedAccount.id !== ALL_ACCOUNTS_ID ? selectedAccount.id : 0;

      await apiService.saveJournal({
        cex_account_id: accId,
        trade_date: tradeDate,
        remarks: journalRemarks,
        screenshots: journalScreenshots,
      }, formData.token);

      setJournalSaveSuccess(true);
      setIsEditingDate(false);

      // Clear success message after 3 seconds
      setTimeout(() => setJournalSaveSuccess(false), 3000);
    } catch (e: any) {
      setJournalSaveError(e.message || 'Failed to save journal entry.');
    } finally {
      setIsSavingJournal(false);
    }
  };

  const handleScreenshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    setJournalSaveError('');

    for (const file of Array.from(files) as File[]) {
      if (file.size > 2 * 1024 * 1024) {
        setJournalSaveError('Screenshot size must be less than 2MB');
        continue;
      }

      const fd = new FormData();
      fd.append('file', file);

      try {
        const response = await fetch(`${API_BASE_URL}/api/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: fd,
        });

        const data = await response.json();
        if (response.ok) {
          setJournalScreenshots(prev => [...prev, data.url]);
        } else {
          setJournalSaveError(data.message || 'Failed to upload screenshot');
        }
      } catch (err) {
        console.error(err);
        setJournalSaveError('Error connecting to upload server');
      }
    }
  };

  const handleRemoveScreenshot = (index: number) => {
    setJournalScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  // Load journal when date is selected
  useEffect(() => {
    if (selectedDate && formData.token) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate).padStart(2, '0');
      const tradeDate = `${year}-${month}-${day}`;

      const accId = selectedAccount && selectedAccount.id !== ALL_ACCOUNTS_ID ? selectedAccount.id : undefined;

      apiService.getJournal(tradeDate, formData.token, accId)
        .then(response => {
          if (response.success && response.data) {
            setJournalRemarks(response.data.remarks || '');
            setJournalScreenshots(response.data.screenshots || []);
          } else {
            setJournalRemarks('');
            setJournalScreenshots([]);
          }
        })
        .catch(() => {
          setJournalRemarks('');
          setJournalScreenshots([]);
        });
    }
  }, [selectedDate, currentDate, formData.token, selectedAccount]);

  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  // 100% Figma Match: Mock Portfolio Data for Demo Simulator
  const mockPortfolioData = React.useMemo(() => ({
    totalBalance: "$142,508.42",
    netPnl: "+$15,640.22",
    netPnlPercent: "+12.4%",
    avgWinLoss: "+$42,120.00 / -$18,440.50",
    returnOnLong: "+$28,900.00",
    returnOnShort: "+$4,779.50",
    highcapPnl: "+$15,200.00",
    midcapPnl: "+$8,400.00",
    lowcapPnl: "-$1,200.00",
    performance: [
      { date: "1", dayName: "sun", realizedValue: 0, positive: false, realized: "$0.00", tradesCount: 0, neutral: true },
      { date: "2", dayName: "mon", realizedValue: 12000, positive: true, realized: "+$12,000.00", tradesCount: 2, neutral: false },
      { date: "3", dayName: "tue", realizedValue: 18000, positive: true, realized: "+$18,000.00", tradesCount: 3, neutral: false },
      { date: "4", dayName: "wed", realizedValue: -2400, positive: false, realized: "-$2,400.00", tradesCount: 1, neutral: false },
      { date: "5", dayName: "thu", realizedValue: 9400, positive: true, realized: "+$9,400.00", tradesCount: 2, neutral: false },
      { date: "6", dayName: "fri", realizedValue: 16000, positive: true, realized: "+$16,000.00", tradesCount: 4, neutral: false },
      { date: "7", dayName: "sat", realizedValue: 0, positive: false, realized: "$0.00", tradesCount: 0, neutral: true },
    ],
  }), []);

  const displayPortfolioData = isDemoMode ? mockPortfolioData : portfolioData;

  const balance = displayPortfolioData?.totalBalance || "$0.00";
  const pnl = displayPortfolioData?.netPnl || "$0.00";
  const pnlPercent = displayPortfolioData?.netPnlPercent || "0%";
  const pnlPositive = parseFloat(pnl.replace(/[^0-9.-]+/g, "")) >= 0;

  // Dynamic calculation of reports page setups, indicators, sessions, and timeframes
  const displayStats = React.useMemo(() => {
    if (isDemoMode) {
      return {
        setups: [
          { name: 'Breakout', total: 6, win: 5, loss: 1, winRate: 85, value: '$18,200.00' },
          { name: 'Pullback', total: 6, win: 4, loss: 2, winRate: 72, value: '$12,100.00' },
          { name: 'Trend Continuation', total: 5, win: 3, loss: 2, winRate: 64, value: '$9,400.00' },
          { name: 'Reversal', total: 4, win: 1, loss: 3, winRate: 25, value: '-$2,400.00' },
          { name: 'News/Event', total: 2, win: 1, loss: 1, winRate: 50, value: '$800.00' }
        ],
        indicators: [
          { name: 'EMA Crossover', code: 'EMA', total: 11, win: 8, loss: 3, winRate: 72 },
          { name: 'Order Block', code: 'OB', total: 8, win: 6, loss: 2, winRate: 75 },
          { name: 'RSI Divergence', code: 'RSI', total: 6, win: 4, loss: 2, winRate: 66 },
          { name: 'Volume Spike', code: 'VOL', total: 4, win: 2, loss: 2, winRate: 50 }
        ],
        sessions: [
          { name: 'Morning', win: 4, loss: 1, winRate: 80 },
          { name: 'Afternoon', win: 3, loss: 2, winRate: 60 },
          { name: 'Evening', win: 5, loss: 3, winRate: 62 },
          { name: 'Night', win: 2, loss: 2, winRate: 50 }
        ],
        timeframes: [
          { name: '15m', win: 3, loss: 1, winRate: 75 },
          { name: '1H', win: 4, loss: 2, winRate: 66 },
          { name: '4H', win: 5, loss: 2, winRate: 71 },
          { name: 'Daily', win: 2, loss: 1, winRate: 66 },
          { name: 'Weekly', win: 1, loss: 0, winRate: 100 }
        ]
      };
    }

    const setupMap: Record<string, { total: number; win: number; loss: number }> = {};
    const indicatorMap: Record<string, { total: number; win: number; loss: number }> = {};
    const sessionMap: Record<string, { total: number; win: number; loss: number }> = {};
    const timeframeMap: Record<string, { total: number; win: number; loss: number }> = {};

    (trades || []).forEach(t => {
      const isWin = t.status === 'WIN';
      const isLoss = t.status === 'LOSS' || t.status === 'LOSE';

      if (t.primary_setup_type) {
        t.primary_setup_type.split(',').forEach((s: string) => {
          const name = s.trim();
          if (!name) return;
          if (!setupMap[name]) setupMap[name] = { total: 0, win: 0, loss: 0 };
          setupMap[name].total++;
          if (isWin) setupMap[name].win++;
          if (isLoss) setupMap[name].loss++;
        });
      }

      if (t.key_indicators) {
        t.key_indicators.split(',').forEach((ind: string) => {
          const name = ind.trim();
          if (!name) return;
          if (!indicatorMap[name]) indicatorMap[name] = { total: 0, win: 0, loss: 0 };
          indicatorMap[name].total++;
          if (isWin) indicatorMap[name].win++;
          if (isLoss) indicatorMap[name].loss++;
        });
      }

      if (t.session) {
        const name = t.session.trim();
        if (name) {
          if (!sessionMap[name]) sessionMap[name] = { total: 0, win: 0, loss: 0 };
          sessionMap[name].total++;
          if (isWin) sessionMap[name].win++;
          if (isLoss) sessionMap[name].loss++;
        }
      }

      if (t.timeframe_analysis) {
        t.timeframe_analysis.split(',').forEach((tf: string) => {
          const name = tf.trim();
          if (!name) return;
          if (!timeframeMap[name]) timeframeMap[name] = { total: 0, win: 0, loss: 0 };
          timeframeMap[name].total++;
          if (isWin) timeframeMap[name].win++;
          if (isLoss) timeframeMap[name].loss++;
        });
      }
    });

    const setups = Object.entries(setupMap).map(([name, stats]) => ({
      name,
      total: stats.total,
      win: stats.win,
      loss: stats.loss,
      winRate: stats.total > 0 ? Math.round((stats.win / stats.total) * 100) : 0,
      value: `${stats.win} Win / ${stats.loss} Loss`
    })).sort((a, b) => b.winRate - a.winRate);

    const indicators = Object.entries(indicatorMap).map(([name, stats]) => {
      let code = name.substring(0, 3).toUpperCase();
      if (name.toLowerCase().includes('ema')) code = 'EMA';
      else if (name.toLowerCase().includes('rsi')) code = 'RSI';
      else if (name.toLowerCase().includes('vol')) code = 'VOL';
      else if (name.toLowerCase().includes('block')) code = 'OB';
      else if (name.toLowerCase().includes('liquid')) code = 'LIQ';
      return {
        name,
        code,
        total: stats.total,
        win: stats.win,
        loss: stats.loss,
        winRate: stats.total > 0 ? Math.round((stats.win / stats.total) * 100) : 0
      };
    }).sort((a, b) => b.winRate - a.winRate);

    const sessions = Object.entries(sessionMap).map(([name, stats]) => ({
      name,
      win: stats.win,
      loss: stats.loss,
      winRate: stats.total > 0 ? Math.round((stats.win / stats.total) * 100) : 0
    })).sort((a, b) => b.winRate - a.winRate);

    const timeframes = Object.entries(timeframeMap).map(([name, stats]) => ({
      name,
      win: stats.win,
      loss: stats.loss,
      winRate: stats.total > 0 ? Math.round((stats.win / stats.total) * 100) : 0
    })).sort((a, b) => b.winRate - a.winRate);

    return { setups, indicators, sessions, timeframes };
  }, [trades, isDemoMode]);

  // Calculate dynamic biggest profit / loss
  const biggestProfitTrade = React.useMemo(() => {
    if (!trades || trades.length === 0) return null;
    const validTrades = trades.filter(t => (t.pnl_amount || 0) > 0);
    if (validTrades.length === 0) return null;
    return validTrades.reduce((max, t) => (t.pnl_amount || 0) > (max.pnl_amount || 0) ? t : max, validTrades[0]);
  }, [trades]);

  const biggestLossTrade = React.useMemo(() => {
    if (!trades || trades.length === 0) return null;
    const validTrades = trades.filter(t => (t.pnl_amount || 0) < 0);
    if (validTrades.length === 0) return null;
    return validTrades.reduce((min, t) => (t.pnl_amount || 0) < (min.pnl_amount || 0) ? t : min, validTrades[0]);
  }, [trades]);

  // Calendar Day Trades details
  const selectedDayTrades = React.useMemo(() => {
    if (!selectedDate || !trades) return [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = parseInt(selectedDate);

    return trades.filter(t => {
      if (!t.trade_date) return false;
      const tDate = new Date(t.trade_date);
      return tDate.getFullYear() === year &&
             tDate.getMonth() === month &&
             tDate.getDate() === day;
    });
  }, [selectedDate, currentDate, trades]);

  const formattedModalDate = React.useMemo(() => {
    if (!selectedDate) return '';
    if (isDemoMode) {
      return 'Friday, Sept 15';
    }
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = parseInt(selectedDate);
    const dateObj = new Date(year, month, day);
    return dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  }, [selectedDate, currentDate, isDemoMode]);

  const selectedDayStats = React.useMemo(() => {
    if (isDemoMode) {
      return {
        totalGain: 2840.45,
        tradesCount: 6,
        winRate: 66.7,
        grossPnl: 3210.00,
        winners: 4,
        losers: 2,
        comms: 24.50,
        tradesList: [
          { time: '11:15:42', symbol: 'ETH/USD', ticker: 'QQQ', side: 'SHORT', volume: 500, pnl: -320.00, roi: -0.12 },
          { time: '12:03:19', symbol: 'ADA/BTC', ticker: 'DIA', side: 'LONG', volume: 750, pnl: 450.00, roi: 0.05 },
          { time: '13:47:33', symbol: 'XRP/USDT', ticker: 'IWM', side: 'LONG', volume: 1200, pnl: 610.00, roi: 0.08 },
          { time: '15:20:10', symbol: 'SOL/USDT', ticker: 'SOL', side: 'LONG', volume: 800, pnl: 1250.00, roi: 0.15 },
          { time: '16:45:15', symbol: 'BTC/USDT', ticker: 'BTC', side: 'LONG', volume: 150, pnl: 900.00, roi: 0.09 },
          { time: '18:10:05', symbol: 'DOGE/USDT', ticker: 'DOGE', side: 'SHORT', volume: 2000, pnl: -50.00, roi: -0.02 },
        ]
      };
    }

    const tList = selectedDayTrades.map(t => {
      const pnlVal = t.pnl_amount || 0;
      const entryVal = t.entry_price || 1;
      const roiVal = entryVal > 0 ? pnlVal / entryVal : 0;
      const dateObj = new Date(t.trade_date);
      return {
        time: dateObj.toLocaleTimeString('en-US', { hour12: false }),
        symbol: t.pairs || 'Unknown',
        ticker: t.pairs?.split('/')?.[0] || 'Asset',
        side: t.direction || 'BUY',
        volume: t.position_size || 0,
        pnl: pnlVal,
        roi: roiVal
      };
    });

    const totalCount = tList.length;
    const wins = tList.filter(t => t.pnl > 0).length;
    const losses = tList.filter(t => t.pnl < 0).length;
    const winRate = totalCount > 0 ? Math.round((wins / totalCount) * 100) : 0;
    const grossPnl = tList.reduce((sum, t) => sum + t.pnl, 0);

    return {
      totalGain: grossPnl,
      tradesCount: totalCount,
      winRate,
      grossPnl,
      winners: wins,
      losers: losses,
      comms: 0,
      tradesList: tList
    };
  }, [selectedDayTrades, isDemoMode]);

  // Calculations for PDF report line chart
  const { points, pathD, areaD } = React.useMemo(() => {
    const perf = displayPortfolioData?.performance || [];
    let cumPnl = 0;
    const points = perf.map((day: any, idx: number) => {
      cumPnl += day.realizedValue || 0;
      return { x: idx, y: cumPnl };
    });
    
    const svgWidth = 710;
    const svgHeight = 180;
    
    if (points.length === 0) {
      return { points, pathD: '', areaD: '' };
    }
    
    let minX = 0;
    let maxX = Math.max(1, points.length - 1);
    let minY = Math.min(...points.map((p: any) => p.y), 0);
    let maxY = Math.max(...points.map((p: any) => p.y), 100);
    
    const yRange = maxY - minY;
    minY -= yRange * 0.1 || 10;
    maxY += yRange * 0.1 || 10;
    
    const mapX = (x: number) => (x / maxX) * svgWidth;
    const mapY = (y: number) => svgHeight - ((y - minY) / (maxY - minY)) * svgHeight;
    
    const pathD = `M ${mapX(points[0].x)} ${mapY(points[0].y)} ` + points.slice(1).map((p: any) => `L ${mapX(p.x)} ${mapY(p.y)}`).join(' ');
    const areaD = `${pathD} L ${mapX(points[points.length - 1].x)} ${svgHeight} L 0 ${svgHeight} Z`;
    
    return { points, pathD, areaD };
  }, [displayPortfolioData]);

  const { maxWins, maxLosses } = React.useMemo(() => {
    let maxWins = 0;
    let maxLosses = 0;
    let currentWins = 0;
    let currentLosses = 0;
    
    const sortedTrades = [...trades].sort((a, b) => a.timestamp - b.timestamp);
    
    sortedTrades.forEach(t => {
      const pnlVal = t.pnl_amount || 0;
      if (pnlVal > 0) {
        currentWins++;
        currentLosses = 0;
        if (currentWins > maxWins) maxWins = currentWins;
      } else if (pnlVal < 0) {
        currentLosses++;
        currentWins = 0;
        if (currentLosses > maxLosses) maxLosses = currentLosses;
      }
    });
    return { maxWins, maxLosses };
  }, [trades]);

  const { grossProfit, grossLoss, totalTradesCount, winRateStr, profitFactorStr } = React.useMemo(() => {
    let grossProfit = 0;
    let grossLoss = 0;
    let wins = 0;
    
    trades.forEach(t => {
      const pnlVal = t.pnl_amount || 0;
      if (pnlVal > 0) {
        grossProfit += pnlVal;
        wins++;
      } else if (pnlVal < 0) {
        grossLoss += Math.abs(pnlVal);
      }
    });
    
    const totalTradesCount = trades.length;
    const winRateStr = totalTradesCount > 0 ? `${Math.round((wins / totalTradesCount) * 100)}%` : '0%';
    const profitFactorStr = grossLoss > 0 ? (grossProfit / grossLoss).toFixed(2) : (grossProfit > 0 ? '99.99' : '0.00');
    
    return { grossProfit, grossLoss, totalTradesCount, winRateStr, profitFactorStr };
  }, [trades]);

  const { longCount, longWins, longPnl, shortCount, shortWins, shortPnl } = React.useMemo(() => {
    let longCount = 0;
    let longWins = 0;
    let longPnl = 0;
    let shortCount = 0;
    let shortWins = 0;
    let shortPnl = 0;
    
    trades.forEach(t => {
      const pnlVal = t.pnl_amount || 0;
      const isLong = t.direction === 'LONG' || t.direction === 'BUY' || (t.direction !== 'SHORT' && t.direction !== 'SELL' && pnlVal >= 0);
      if (isLong) {
        longCount++;
        longPnl += pnlVal;
        if (pnlVal > 0) longWins++;
      } else {
        shortCount++;
        shortPnl += pnlVal;
        if (pnlVal > 0) shortWins++;
      }
    });
    
    return { longCount, longWins, longPnl, shortCount, shortWins, shortPnl };
  }, [trades]);

  const symbolStats = React.useMemo(() => {
    const map: Record<string, { total: number; wins: number; pnl: number; grossProfit: number; grossLoss: number }> = {};
    trades.forEach(t => {
      const sym = t.pairs || 'Unknown';
      const pnlVal = t.pnl_amount || 0;
      if (!map[sym]) {
        map[sym] = { total: 0, wins: 0, pnl: 0, grossProfit: 0, grossLoss: 0 };
      }
      map[sym].total++;
      map[sym].pnl += pnlVal;
      if (pnlVal > 0) {
        map[sym].wins++;
        map[sym].grossProfit += pnlVal;
      } else if (pnlVal < 0) {
        map[sym].grossLoss += Math.abs(pnlVal);
      }
    });
    
    return Object.entries(map).map(([symbol, stats]) => {
      const winRatePercent = stats.total > 0 ? Math.round((stats.wins / stats.total) * 100) : 0;
      const pf = stats.grossLoss > 0 ? (stats.grossProfit / stats.grossLoss).toFixed(2) : (stats.grossProfit > 0 ? '99.99' : '0.00');
      return {
        symbol,
        total: stats.total,
        winRate: winRatePercent,
        pnl: stats.pnl,
        profitFactor: pf
      };
    }).sort((a, b) => b.pnl - a.pnl);
  }, [trades]);

  const exportPDF = async () => {
    setIsExporting(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageIds = ['pdf-page-1', 'pdf-page-2', 'pdf-page-3'];
      
      for (let i = 0; i < pageIds.length; i++) {
        const el = document.getElementById(pageIds[i]);
        if (!el) continue;
        
        el.style.position = 'relative';
        el.style.left = '0';
        el.style.top = '0';
        
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        });
        
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        el.style.top = '-9999px';
        
        const imgData = canvas.toDataURL('image/png');
        if (i > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
      }
      
      pdf.save(`CatatCrypto_Statement_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      <Sidebar plan={formData.plan} />

      <main className="flex-1 bg-[#050505] min-h-screen flex flex-col relative overflow-x-hidden pt-16 md:pt-0">
        <TopBar
          title="Reports"
          name={formData.name}
          showAccountSwitcher={true}
        >
          <div data-tour="reports-tabs" className="hidden md:flex bg-[#0B0C10] p-1 rounded-xl gap-1 border border-[#16171F] ml-2">
            {['Overview', 'Calendar', 'History'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 rounded-lg text-xs font-semibold tracking-wide transition-all duration-300 ${activeTab === tab ? 'bg-[#E50914] text-white shadow-lg shadow-red-950/20' : 'text-neutral-500 hover:text-neutral-300'}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </TopBar>

        {/* Mobile-only tab bar rendered below TopBar */}
        <div className="md:hidden flex bg-[#0B0C10] border-b border-[#16171F] px-3 pt-2 pb-0 gap-1">
          {['Overview', 'Calendar', 'History'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 rounded-t-lg text-[11px] font-semibold tracking-wide transition-all duration-300 ${activeTab === tab ? 'bg-[#E50914] text-white' : 'text-neutral-500 hover:text-neutral-300'}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1">
          {/* Action Header with Export & Demo Toggle */}
          <div className="mx-auto max-w-[1600px] px-3 md:px-8 pt-6 md:pt-8 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 select-none">
            <div>
              <h1 className="text-xl md:text-2xl font-extrabold text-white tracking-tight font-sans">
                Performance Statement
              </h1>
              <p className="text-xs text-neutral-400 mt-1 font-sans">
                Generate and export institutional-grade MetaTrader-style performance audit reports.
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Demo/Live Toggle Switch */}
              <div className="bg-[#0B0C10] p-1 rounded-xl flex gap-1 border border-[#16171F] select-none h-10 items-center">
                <button
                  onClick={() => setIsDemoMode(false)}
                  className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-300 h-full flex items-center cursor-pointer ${!isDemoMode ? 'bg-[#22C55E] text-black shadow-md shadow-green-950/20' : 'text-neutral-500 hover:text-neutral-300'}`}
                >
                  Live
                </button>
                <button
                  onClick={() => setIsDemoMode(true)}
                  className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-300 h-full flex items-center cursor-pointer ${isDemoMode ? 'bg-[#E50914] text-white shadow-md shadow-red-950/20' : 'text-neutral-500 hover:text-neutral-300'}`}
                >
                  Demo
                </button>
              </div>

              {/* Export PDF Button */}
              <button
                onClick={exportPDF}
                disabled={isExporting || isLoadingTrades}
                className={`sm:w-auto px-5 py-2.5 border text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 cursor-pointer h-10 ${
                  document.documentElement.classList.contains('light')
                    ? 'bg-white hover:bg-gray-50 border-gray-300 text-black'
                    : 'bg-[#1b1c24] hover:bg-[#252733] border-[#2d2f3d] text-white'
                }`}
              >
                {isExporting ? (
                  <>
                    <Loader2 className="animate-spin text-[#E50914]" size={14} />
                    <span>Exporting PDF...</span>
                  </>
                ) : (
                  <>
                    <FileDown size={14} className={document.documentElement.classList.contains('light') ? 'text-gray-600' : 'text-neutral-400'} />
                    <span>Export PDF Report</span>
                  </>
                )}
              </button>
            </div>
          </div>
                    {/* Preview Mode Notification Banner */}
          {isDemoMode && (
            <div className="mx-auto max-w-[1600px] px-3 md:px-8 pt-4 md:pt-8 font-sans select-none">
              <div className="relative overflow-hidden bg-gradient-to-r from-[#0B0C10] to-[#12131A] border border-[#EF4444]/20 rounded-3xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-[0_0_50px_rgba(229,9,20,0.04)]">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#E50914]/5 rounded-full blur-[80px] pointer-events-none" />
                
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-12 h-12 rounded-2xl bg-[#EF4444]/10 border border-[#EF4444]/20 flex items-center justify-center text-[#EF4444] shrink-0 shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                    <Activity size={22} className="animate-pulse" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold tracking-wide text-white">Preview Mode Active</h4>
                    <p className="text-xs text-neutral-400 mt-1 max-w-xl">
                      Showing simulated high-fidelity demo data. Try adding a manual trade or syncing your exchange account to unlock your real performance metrics!
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3 shrink-0 relative z-10">
                  <button 
                    onClick={() => { window.location.href = '/trades?add=true'; }}
                    className="px-5 py-2.5 rounded-xl bg-[#E50914] hover:bg-[#E50914]/90 text-white text-xs font-bold transition-all shadow-lg hover:scale-105"
                  >
                    Add Manual Trade
                  </button>
                  <button 
                    onClick={() => handleResync(true, false)}
                    className="px-5 py-2.5 rounded-xl bg-[#0E0F14] border border-[#181922] hover:border-neutral-700 text-neutral-300 hover:text-white text-xs font-bold transition-all hover:scale-105"
                  >
                    Sync CEX Account
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sync Error Alert */}
          {syncError && (
            <div className="p-3 md:p-8 pb-0 max-w-[1600px] mx-auto">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-900/20 border border-red-900/50 rounded-2xl p-4 flex items-center gap-3"
              >
                <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-500">{syncError}</p>
                  <p className="text-xs text-neutral-400 mt-1">Try re-syncing or check your API settings.</p>
                </div>
                <button onClick={() => setSyncError('')} className="text-neutral-500 hover:text-white transition-colors">×</button>
              </motion.div>
            </div>
          )}

          {activeTab === 'Overview' && (
            <div className="p-3 md:p-8 max-w-[1600px] mx-auto space-y-6 md:space-y-8 animate-in fade-in duration-500 font-sans">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Account Balance Card with dynamic sparkline */}
                <div className="lg:col-span-5">
                  <div data-tour="reports-balance" className="bg-[#0B0C10] border border-[#16171F] rounded-[32px] p-6 md:p-10 relative overflow-hidden flex flex-col justify-between min-h-[260px] md:h-[340px] shadow-[0_10px_35px_rgba(0,0,0,0.3)] select-none">
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-neutral-500 tracking-wider mb-2">Account Balance</p>
                      <h2 className="text-5xl font-bold text-white tracking-tight mb-2 font-sans" style={{ fontVariantNumeric: 'tabular-nums', ...getDynamicFontSizeStyle(balance, 32, 48) }}>{balance}</h2>
                      <div className={`flex items-center gap-1.5 text-xs font-bold ${pnlPositive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                        <TrendingUp size={14} className={pnlPositive ? '' : 'rotate-180'} /> {pnlPercent} this month
                      </div>
                    </div>

                    <div className="flex items-end gap-2 h-24 relative z-10">
                      {displayPortfolioData?.performance?.slice(-10).map((day, i) => {
                        const maxVal = Math.max(...displayPortfolioData.performance.map(d => Math.abs(d.realizedValue || 0)));
                        const h = maxVal === 0 ? 10 : Math.max(10, (Math.abs(day.realizedValue || 0) / maxVal) * 100);
                        return (
                          <motion.div
                            key={i}
                            initial={{ height: 0 }}
                            animate={{ height: `${h}%` }}
                            transition={{ delay: i * 0.05, duration: 0.5 }}
                            className={`flex-1 rounded-t-lg transition-all duration-300 ${day.positive ? 'bg-gradient-to-t from-[#22C55E]/30 to-[#22C55E] shadow-[0_0_8px_rgba(34,197,94,0.3)]' : 'bg-[#181922] hover:bg-neutral-800'}`}
                          />
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* Quick Stats Grid */}
                <div data-tour="reports-stats" className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  <MiniStatCard label="Return on Winners" value={displayPortfolioData?.avgWinLoss?.split(' / ')[0] || "$0"} positive />
                  <MiniStatCard label="Return on Losers" value={displayPortfolioData?.avgWinLoss?.split(' / ')[1] || "$0"} />
                  
                  <MiniStatCard
                    label="Return on Long"
                    value={displayPortfolioData?.returnOnLong || "+$0.00"}
                    positive={parseFloat(displayPortfolioData?.returnOnLong?.replace(/[^0-9.-]+/g, "") || "0") >= 0}
                  />
                  <MiniStatCard
                    label="Return on Short"
                    value={displayPortfolioData?.returnOnShort || "+$0.00"}
                    positive={parseFloat(displayPortfolioData?.returnOnShort?.replace(/[^0-9.-]+/g, "") || "0") >= 0}
                  />

                  <MiniStatCard 
                    label="Biggest Profit" 
                    value={isDemoMode ? "+$12,400.00" : (biggestProfitTrade ? `+$${biggestProfitTrade.pnl_amount.toLocaleString('en-US', {minimumFractionDigits: 2})}` : "$0.00")} 
                    subValue={isDemoMode ? "BTC/USDT 20x" : (biggestProfitTrade ? `${biggestProfitTrade.pairs || 'Unknown'} (${biggestProfitTrade.cex_account_id ? 'Auto Sync' : 'Manual'})` : "No trades")} 
                    positive 
                  />
                  <MiniStatCard 
                    label="Biggest Loss" 
                    value={isDemoMode ? "-$3,200.00" : (biggestLossTrade ? `-$${Math.abs(biggestLossTrade.pnl_amount).toLocaleString('en-US', {minimumFractionDigits: 2})}` : "$0.00")} 
                    subValue={isDemoMode ? "PEPE/USDT 50x" : (biggestLossTrade ? `${biggestLossTrade.pairs || 'Unknown'} (${biggestLossTrade.cex_account_id ? 'Auto Sync' : 'Manual'})` : "No trades")} 
                  />

                  <MiniStatCard
                    label="Highcap PNL"
                    value={displayPortfolioData?.highcapPnl || "$0.00"}
                    positive={parseFloat(displayPortfolioData?.highcapPnl?.replace(/[^0-9.-]+/g, "") || "0") >= 0}
                  />
                  <MiniStatCard
                    label="Midcap PNL"
                    value={displayPortfolioData?.midcapPnl || "$0.00"}
                    positive={parseFloat(displayPortfolioData?.midcapPnl?.replace(/[^0-9.-]+/g, "") || "0") >= 0}
                  />
                  <MiniStatCard
                    label="Lowcap PNL"
                    value={displayPortfolioData?.lowcapPnl || "$0.00"}
                    positive={parseFloat(displayPortfolioData?.lowcapPnl?.replace(/[^0-9.-]+/g, "") || "0") >= 0}
                  />
                </div>
              </div>

              {/* Middle Row Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profit by Day Bar Chart */}
                <ReportCard title="Profit by Day" icon={<Calendar size={16} className="text-[#E50914]" />}>
                  <div className="flex items-end justify-between gap-2.5 h-48 mt-8 select-none">
                    {displayPortfolioData?.performance && displayPortfolioData.performance.length >= 7 ? (
                      displayPortfolioData.performance.slice(-7).map((day, i) => {
                        const maxVal = Math.max(...displayPortfolioData.performance.map(d => Math.abs(d.realizedValue || 0)));
                        const h = maxVal === 0 ? 10 : Math.max(10, (Math.abs(day.realizedValue || 0) / maxVal) * 100);
                        return (
                          <DayBar key={i} height={`${h}%`} day={day.dayName.toUpperCase()} positive={day.positive} />
                        );
                      })
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-neutral-600 text-xs font-semibold">No data available</div>
                    )}
                  </div>
                </ReportCard>

                {/* Best Primary Setup */}
                <ReportCard title="Best Primary Setup" icon={<Activity size={16} className="text-[#E50914]" />}>
                  <div className="space-y-4 mt-8">
                    {displayStats.setups.length > 0 ? (
                      displayStats.setups.slice(0, 3).map(s => (
                        <SetupRow key={s.name} label={s.name} value={s.value} percent={`${s.winRate}%`} />
                      ))
                    ) : (
                      <p className="text-neutral-500 text-xs italic py-4 text-center">No setup data available. Tag setup types in Trades.</p>
                    )}
                  </div>
                </ReportCard>

                {/* Best Indicator */}
                <ReportCard title="Best Indicator" icon={<TrendingUp size={16} className="text-[#E50914]" />}>
                  <div className="space-y-4 mt-8 max-h-[220px] overflow-y-auto pr-1" data-lenis-prevent>
                    {displayStats.indicators.length > 0 ? (
                      displayStats.indicators.slice(0, 3).map(ind => (
                        <IndicatorRow key={ind.name} code={ind.code} label={ind.name} winRate={`${ind.win} Win`} percent={`${ind.winRate}%`} />
                      ))
                    ) : (
                      <p className="text-neutral-500 text-xs italic py-4 text-center">No indicator data available. Add indicators in Trades.</p>
                    )}
                  </div>
                </ReportCard>
              </div>

              {/* Grid 2 - Session Stats & Timeframe Performance */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ReportCard title="Trading Sessions" icon={<Activity size={16} className="text-[#E50914]" />}>
                  <div className="space-y-5 mt-8">
                    {displayStats.sessions.length > 0 ? (
                      displayStats.sessions.map(sess => (
                        <SetupRow key={sess.name} label={sess.name} value={`${sess.win} Win / ${sess.loss} Lose`} percent={`${sess.winRate}%`} />
                      ))
                    ) : (
                      <p className="text-neutral-500 text-xs italic py-4 text-center">No session data available. Set sessions in Trades.</p>
                    )}
                  </div>
                </ReportCard>

                <ReportCard title="Timeframe Performance" icon={<TrendingUp size={16} className="text-[#E50914]" />}>
                  <div className="space-y-4 mt-8">
                    {displayStats.timeframes.length > 0 ? (
                      displayStats.timeframes.map(tf => (
                        <IndicatorRow key={tf.name} code={tf.name} label={`${tf.name} Timeframe`} winRate={isDemoMode ? `${tf.winRate}% Win` : `${tf.win}W / ${tf.loss}L`} percent={`${tf.winRate}%`} />
                      ))
                    ) : (
                      <p className="text-neutral-500 text-xs italic py-4 text-center">No timeframe data available. Set timeframes in Trades.</p>
                    )}
                  </div>
                </ReportCard>
              </div>

              {/* Hold Time Bubble Chart */}
              <div className="bg-[#0B0C10] border border-[#16171F] rounded-[32px] p-5 md:p-10 relative shadow-[0_10px_35px_rgba(0,0,0,0.2)]">
                <div className="flex justify-between items-center mb-12 select-none">
                  <div className="space-y-1">
                    <h3 className="text-lg font-bold tracking-tight text-white">Average Hold Time vs PnL</h3>
                    <p className="text-neutral-500 text-xs font-medium">Correlation between trade duration and profitability</p>
                  </div>
                  <div className="flex gap-5 items-center">
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#22C55E] select-none">
                      <div className="w-2 h-2 bg-[#22C55E] rounded-full" /> Profitable
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#EF4444] select-none">
                      <div className="w-2 h-2 bg-[#EF4444] rounded-full" /> Unprofitable
                    </div>
                  </div>
                </div>

                <div className="h-40 md:h-64 flex justify-between items-stretch gap-2 px-2 md:px-6 mt-8 select-none">
                  {isDemoMode ? (
                    <>
                      <HoldBubble label="+$2,000" subLabel="-$4,000" duration="0-2H" posHeight="44px" negHeight="32px" />
                      <HoldBubble label="+$12,000" duration="2-6H" posHeight="76px" />
                      <HoldBubble label="+$4,000" duration="6-12H" posHeight="52px" />
                      <HoldBubble subLabel="-$8,000" duration="12-24H" negHeight="58px" />
                      <HoldBubble label="+$18,000" duration="24H+" posHeight="94px" />
                    </>
                  ) : isLoadingHoldTime ? (
                    <div className="flex-1 flex items-center justify-center text-neutral-500 text-xs font-semibold">
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Loading hold time data...
                    </div>
                  ) : holdTimeData?.ranges ? (
                    <>
                      {holdTimeData.ranges.map((range: any) => {
                        const maxPnl = Math.max(
                          ...holdTimeData.ranges.map((r: any) => 
                            Math.max(Math.abs(r.profitable_pnl), Math.abs(r.unprofitable_pnl))
                          )
                        );
                        const posHeight = range.profitable_pnl > 0 
                          ? `${Math.max(20, (Math.abs(range.profitable_pnl) / maxPnl) * 100)}px`
                          : undefined;
                        const negHeight = range.unprofitable_pnl < 0 
                          ? `${Math.max(20, (Math.abs(range.unprofitable_pnl) / maxPnl) * 100)}px`
                          : undefined;
                        
                        return (
                          <HoldBubble
                            key={range.duration}
                            label={range.profitable_pnl > 0 ? `+$${range.profitable_pnl.toFixed(2)}` : undefined}
                            subLabel={range.unprofitable_pnl < 0 ? `-$${Math.abs(range.unprofitable_pnl).toFixed(2)}` : undefined}
                            duration={range.duration}
                            posHeight={posHeight}
                            negHeight={negHeight}
                          />
                        );
                      })}
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-neutral-600 text-xs font-semibold">
                      No hold time data available
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Calendar' && (
            <div className="p-3 md:p-8 max-w-[1600px] mx-auto h-full flex flex-col animate-in fade-in slide-in-from-right-4 duration-500 font-sans">
              <div className="flex justify-between items-center mb-6 shrink-0 select-none">
                <div>
                  <h2 className="text-3xl font-bold tracking-tight">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
                  <p className="text-neutral-400 text-xs font-medium mt-1">
                    Monthly PnL: <span className="text-[#22C55E] font-bold">{pnl}</span> • Winrate: <span className="font-bold text-white">{isDemoMode ? "72%" : (portfolioData?.tradeWinRate || '0%')}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={handlePrevMonth} className="w-10 h-10 bg-[#0B0C10] border border-[#16171F] rounded-xl flex items-center justify-center hover:bg-neutral-800 transition-colors">
                    <ChevronLeft size={16} />
                  </button>
                  <button onClick={() => setCurrentDate(new Date())} className="px-6 h-10 bg-[#0B0C10] border border-[#16171F] rounded-xl font-bold text-sm hover:bg-neutral-800 transition-colors">
                    Today
                  </button>
                  <button onClick={handleNextMonth} className="w-10 h-10 bg-[#0B0C10] border border-[#16171F] rounded-xl flex items-center justify-center hover:bg-neutral-800 transition-colors">
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="flex-1 bg-[#0B0C10] rounded-[24px] border border-[#16171F] flex flex-col overflow-x-auto shadow-[0_10px_35px_rgba(0,0,0,0.3)]">
                <div className="grid grid-cols-7 border-b border-[#16171F] bg-[#0E0F14] select-none">
                  {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                    <div key={day} className="py-4 text-center text-[10px] font-bold uppercase text-neutral-500 tracking-widest border-r border-[#16171F] last:border-0">{day}</div>
                  ))}
                </div>
                <div className="flex-1 grid grid-cols-7 grid-rows-5 divide-x divide-y divide-[#16171F]">
                  {Array.from({ length: startOffset }).map((_, idx) => {
                    const prevMonthLastDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
                    const date = prevMonthLastDay - startOffset + idx + 1;
                    return <CalendarCell key={`empty-${idx}`} date={date.toString()} disabled />;
                  })}

                  {Array.from({ length: daysInMonth }).map((_, idx) => {
                    const dayNum = idx + 1;
                    const isCurrentMonth = isDemoMode || (currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear());
                    const dayData = isCurrentMonth ? displayPortfolioData?.performance?.find(d => parseInt(d.date) === dayNum) : null;

                    if (dayData) {
                      return (
                        <CalendarCell
                          key={dayNum}
                          date={dayNum.toString()}
                          pnl={dayData.neutral ? null : dayData.realized}
                          trades={dayData.tradesCount > 0 ? `${dayData.tradesCount} TRADES` : null}
                          positive={dayData.positive}
                          active={selectedDate === dayNum.toString()}
                          onClick={() => setSelectedDate(dayNum.toString())}
                        />
                      );
                    }

                    return (
                      <CalendarCell
                        key={dayNum}
                        date={dayNum.toString()}
                        pnl={null}
                        trades={null}
                        positive={false}
                        active={selectedDate === dayNum.toString()}
                        onClick={() => setSelectedDate(dayNum.toString())}
                      />
                    );
                  })}

                  {Array.from({ length: (startOffset + daysInMonth > 35 ? 42 : 35) - (startOffset + daysInMonth) }).map((_, idx) => (
                    <CalendarCell key={`next-${idx}`} date={(idx + 1).toString()} disabled />
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'History' && (
            <div className="p-3 md:p-8 max-w-[1600px] mx-auto h-full flex flex-col animate-in fade-in slide-in-from-right-4 duration-500 font-sans">
              <div className="flex justify-between items-center mb-6 shrink-0 select-none">
                <div>
                  <h2 className="text-xl md:text-3xl font-bold tracking-tight">Performance History Audit</h2>
                  <p className="text-neutral-400 text-xs font-medium mt-1">
                    Audit log of your daily trading performance and journal status
                  </p>
                </div>
              </div>

              <div className="bg-[#0B0C10] rounded-[24px] border border-[#16171F] overflow-x-auto shadow-[0_10px_35px_rgba(0,0,0,0.3)]">
                <table className="w-full text-left border-collapse font-sans">
                  <thead className="bg-[#0E0F14] text-[10px] uppercase font-bold text-neutral-500 tracking-wider border-b border-[#16171F] select-none">
                    <tr>
                      <th className="px-6 py-4">Date</th>
                      <th className="px-6 py-4">Day</th>
                      <th className="px-6 py-4">Trades</th>
                      <th className="px-6 py-4 text-right">Realized PnL</th>
                      <th className="px-6 py-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#16171F] text-xs">
                    {displayPortfolioData?.performance && displayPortfolioData.performance.length > 0 ? (
                      displayPortfolioData.performance.map((day, idx) => (
                        <tr key={idx} className="hover:bg-neutral-900/20 transition-colors">
                          <td className="px-6 py-4 font-bold text-white font-mono">{day.date} {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</td>
                          <td className="px-6 py-4 text-neutral-400 font-bold uppercase text-[10px]">{day.dayName}</td>
                          <td className="px-6 py-4 text-neutral-300 font-bold">{day.tradesCount} trades</td>
                          <td className={`px-6 py-4 text-right font-black font-mono ${day.positive ? 'text-[#22C55E]' : day.neutral ? 'text-neutral-500' : 'text-[#EF4444]'}`}>
                            {day.neutral ? '—' : day.realized}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => setSelectedDate(day.date.toString())}
                              className="px-3.5 py-1.5 bg-[#14151E] hover:bg-neutral-800 border border-[#16171F] rounded-lg font-bold text-[10px] text-neutral-400 hover:text-white uppercase tracking-wider transition-colors active:scale-[0.98]"
                            >
                              Open Journal
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-neutral-500 italic">
                          No performance history available for this month.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Modal for Calendar Detail & Edit (Dynamically Transitions Layout Based on Mode) */}
        <AnimatePresence>
          {selectedDate && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-50 flex items-center justify-center p-8 bg-black/75 backdrop-blur-sm"
              onClick={() => { setSelectedDate(null); setIsEditingDate(false); }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className={`bg-[#0B0C10] border border-[#16171F] rounded-[32px] overflow-hidden shadow-2xl transition-all duration-300 w-full max-h-[90vh] overflow-y-auto ${
                  isEditingDate ? 'max-w-5xl' : 'max-w-4xl'
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Title Row */}
                <div className="px-8 pt-6 pb-3 flex items-center justify-between border-b border-[#16171F]/40 select-none">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black uppercase text-white tracking-widest font-sans">Notes</span>
                    {isEditingDate && (
                      <span className="bg-[#ef4444] text-white text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded font-sans">
                        EDITING
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => { setSelectedDate(null); setIsEditingDate(false); }}
                    className="text-neutral-500 hover:text-white transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>

                {isEditingDate ? (
                  /* ======================================================== */
                  /* EDITING MODE: TWO COLUMN LAYOUT (Figma Split Screen)     */
                  /* ======================================================== */
                  <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-8 divide-y lg:divide-y-0 lg:divide-x divide-[#16171F]/60">
                    
                    {/* LEFT COLUMN: DAILY PERFORMANCE & STATS (Narrower Grid) */}
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-xl font-black text-white tracking-tight leading-none font-sans">
                          {formattedModalDate}
                        </h2>
                        <p className={`text-xs font-bold ${selectedDayStats.totalGain >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'} mt-1.5 font-sans`}>
                          {selectedDayStats.totalGain >= 0 ? `Total Gain: +$${selectedDayStats.totalGain.toFixed(2)}` : `Total Loss: -$${Math.abs(selectedDayStats.totalGain).toFixed(2)}`}
                        </p>
                      </div>

                      <div className="grid grid-cols-3 gap-2.5">
                        <ModalStatCard label="TRADES" value={selectedDayStats.tradesCount} />
                        <ModalStatCard label="WINRATE" value={`${selectedDayStats.winRate}%`} valueColor="text-[#22C55E]" />
                        <ModalStatCard label="GROSS PNL" value={selectedDayStats.grossPnl >= 0 ? `+$${selectedDayStats.grossPnl.toFixed(2)}` : `-$${Math.abs(selectedDayStats.grossPnl).toFixed(2)}`} valueColor={selectedDayStats.grossPnl >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'} />
                        <ModalStatCard label="WINNERS" value={selectedDayStats.winners} />
                        <ModalStatCard label="LOSERS" value={selectedDayStats.losers} />
                        <ModalStatCard label="COMMS" value={`$${selectedDayStats.comms.toFixed(2)}`} />
                      </div>

                      <div className="bg-[#14151E] rounded-xl border border-[#16171F] overflow-hidden max-h-[220px] overflow-y-auto" data-lenis-prevent>
                        <table className="w-full text-left border-collapse font-sans">
                          <thead className="bg-[#0E0F14] text-[8px] uppercase font-bold text-neutral-500 tracking-wider border-b border-[#16171F] select-none">
                            <tr>
                              <th className="px-4 py-2.5">OPEN TIME</th>
                              <th className="px-4 py-2.5">SYMBOL</th>
                              <th className="px-4 py-2.5">TICKER</th>
                              <th className="px-4 py-2.5">SIDE</th>
                              <th className="px-4 py-2.5 text-right">PNL</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#16171F]">
                            {selectedDayStats.tradesList.length > 0 ? (
                              selectedDayStats.tradesList.map((t, idx) => (
                                <tr key={idx} className="hover:bg-neutral-900/20 transition-colors">
                                  <td className="px-4 py-2.5 text-[9px] font-bold text-neutral-400 font-mono">{t.time}</td>
                                  <td className="px-4 py-2.5 text-[9px] font-bold text-white">{t.symbol}</td>
                                  <td className="px-4 py-2.5 text-[9px] font-black text-[#E50914]">{t.ticker}</td>
                                  <td className="px-4 py-2.5">
                                    <span className={`text-[7.5px] font-bold px-1.5 py-0.5 rounded ${t.side === 'LONG' || t.side === 'BUY' ? 'bg-[#3b82f6]/10 text-[#3b82f6]' : 'bg-[#f59e0b]/10 text-[#f59e0b]'}`}>
                                      {t.side}
                                    </span>
                                  </td>
                                  <td className={`px-4 py-2.5 text-[9px] font-bold text-right font-mono ${t.pnl >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                                    {t.pnl >= 0 ? `+$${t.pnl.toFixed(2)}` : `-$${Math.abs(t.pnl).toFixed(2)}`}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={5} className="px-4 py-6 text-center text-[10px] text-neutral-500 italic">No trades executed on this day.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* RIGHT COLUMN: DETAILED JOURNALING (Active Editor) */}
                    <div className="lg:pl-8 pt-6 lg:pt-0 space-y-6 flex flex-col justify-between">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-[#ef4444]">💬</span>
                            <h3 className="text-xs font-black uppercase text-neutral-300 tracking-wider font-sans">
                              Detailed Journaling: {currentDate.toLocaleDateString('en-US', { month: 'short' })} {selectedDate}
                            </h3>
                          </div>
                        </div>

                        {journalSaveError && (
                          <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-xs p-3.5 rounded-xl flex items-center gap-2 font-sans">
                            <AlertCircle size={14} className="flex-shrink-0" />
                            <span>{journalSaveError}</span>
                          </div>
                        )}
                        {journalSaveSuccess && (
                          <div className="bg-green-500/10 border border-green-500/20 text-green-500 text-xs p-3.5 rounded-xl flex items-center gap-2 font-sans">
                            <span>Journal saved successfully!</span>
                          </div>
                        )}

                        <div className="space-y-5">
                          {/* Screenshot Upload grid */}
                          <div className="space-y-2.5">
                            <span className="text-[9px] font-black uppercase text-neutral-500 tracking-wider block font-sans">
                              Trade Setup Screenshots
                            </span>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {journalScreenshots.map((src, index) => {
                                const isCopied = copiedScreenshotIdx === index;
                                const shortUrl = src.length > 25 ? '...' + src.slice(-22) : src;
                                return (
                                  <div key={index} className="rounded-xl border border-neutral-800 overflow-hidden bg-neutral-900/40 group flex flex-col">
                                    <div className="relative aspect-video overflow-hidden bg-neutral-950">
                                      <img src={src} alt="Trade Setup" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                                      {/* Hover Actions Overlay */}
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end justify-between p-2">
                                        <a
                                          href={src}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider text-white bg-white/10 backdrop-blur-sm border border-white/20 px-2 py-1 rounded-md hover:bg-white/20 transition-colors"
                                        >
                                          <Link size={8} />
                                          Open
                                        </a>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveScreenshot(index)}
                                          className="w-5 h-5 bg-red-500/20 border border-red-500/30 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all"
                                        >
                                          <X size={10} />
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {/* CDN URL Bar */}
                                    <div className="px-2 py-1.5 flex items-center justify-between gap-1 border-t border-neutral-850 bg-neutral-950/40">
                                      <div className="flex items-center gap-1 min-w-0 flex-1">
                                        <div className="flex-shrink-0 flex items-center gap-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded px-1 py-0.5">
                                          <div className="w-0.5 h-0.5 rounded-full bg-emerald-400 animate-pulse" />
                                          <span className="text-[6.5px] font-black uppercase tracking-wider text-emerald-400">CDN</span>
                                        </div>
                                        <span className="text-[8px] font-mono text-neutral-500 truncate" title={src}>{shortUrl}</span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          navigator.clipboard.writeText(src);
                                          setCopiedScreenshotIdx(index);
                                          setTimeout(() => setCopiedScreenshotIdx(null), 2000);
                                        }}
                                        className={`flex-shrink-0 p-1 rounded transition-all ${
                                          isCopied
                                            ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                                            : 'bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-neutral-400 hover:text-white'
                                        }`}
                                        title="Copy CDN Link"
                                      >
                                        {isCopied ? <Check size={8} /> : <Copy size={8} />}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                              
                              {/* Add Screenshot button */}
                              <div 
                                onClick={() => document.getElementById('modal-screenshot-input')?.click()}
                                className="aspect-video rounded-xl border border-dashed border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900/10 flex flex-col items-center justify-center cursor-pointer transition-all gap-1.5 select-none group"
                              >
                                <Camera size={14} className="text-neutral-500 group-hover:text-neutral-400 transition-colors" />
                                <span className="text-[8px] font-black text-neutral-500 uppercase tracking-widest text-center leading-none font-sans group-hover:text-neutral-400 transition-colors">
                                  Add Screenshot
                                </span>
                              </div>
                              <input 
                                id="modal-screenshot-input" 
                                type="file" 
                                accept="image/*" 
                                multiple 
                                onChange={handleScreenshotUpload} 
                                className="hidden" 
                              />
                            </div>
                          </div>

                          {/* Daily Remarks & Psychology editor */}
                          <div className="space-y-2.5">
                            <span className="text-[9px] font-black uppercase text-neutral-500 tracking-wider block font-sans">
                              Daily Remarks & Psychology
                            </span>
                            
                            {/* Formatted tools bar */}
                            <div className="flex items-center gap-1 border border-neutral-800 bg-[#0E0F14] rounded-lg p-1 w-max">
                              <span className="p-1 rounded text-neutral-300 bg-neutral-900"><Bold size={11} /></span>
                              <span className="p-1 rounded text-neutral-600 hover:text-neutral-400"><Italic size={11} /></span>
                              <span className="p-1 rounded text-neutral-600 hover:text-neutral-400"><Underline size={11} /></span>
                              <div className="w-px h-3 bg-neutral-800 mx-1" />
                              <span className="p-1 rounded text-neutral-600 hover:text-neutral-400"><AlignLeft size={11} /></span>
                              <span className="p-1 rounded text-neutral-600 hover:text-neutral-400"><List size={11} /></span>
                              <span className="p-1 rounded text-neutral-600 hover:text-neutral-400"><Link size={11} /></span>
                              <span className="p-1 rounded text-neutral-600 hover:text-neutral-400"><ImageIcon size={11} /></span>
                              <span className="p-1 rounded text-neutral-600 hover:text-neutral-400"><Table size={11} /></span>
                            </div>

                            <textarea
                              value={journalRemarks}
                              onChange={(e) => setJournalRemarks(e.target.value)}
                              placeholder="Describe your mental state, market conditions, and mistakes..."
                              className="w-full bg-[#0E0F14] border border-neutral-800 rounded-xl p-3.5 text-xs text-neutral-300 placeholder-neutral-700 focus:outline-none focus:border-red-600/30 min-h-[140px] resize-none font-sans"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Footer Actions */}
                      <div className="flex items-center justify-end gap-3 pt-6 border-t border-[#16171F]/45">
                        <button
                          type="button"
                          onClick={() => setIsEditingDate(false)}
                          className="text-neutral-400 hover:text-white font-black text-[10px] uppercase tracking-wider px-4 py-2 rounded-xl transition-all"
                        >
                          Discard
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveJournal}
                          disabled={isSavingJournal}
                          className="bg-[#ef4444] hover:bg-red-700 text-white font-black text-[10px] uppercase tracking-wider px-5 py-2.5 rounded-xl shadow-lg transition-all"
                        >
                          {isSavingJournal ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>

                  </div>
                ) : (
                  /* ======================================================== */
                  /* READ-ONLY MODE: FULL WIDTH SINGLE COLUMN LAYOUT (Figma Detail) */
                  /* ======================================================== */
                  <div className="p-8 space-y-6 select-none font-sans">
                    
                    {/* Header Row: Date / Gain and Edit Button */}
                    <div className="flex justify-between items-center pb-2 border-b border-[#16171F]/30">
                      <div className="space-y-1">
                        <h2 className="text-xl font-black text-white tracking-tight leading-none">
                          {formattedModalDate}
                        </h2>
                        <p className={`text-xs font-bold ${selectedDayStats.totalGain >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                          {selectedDayStats.totalGain >= 0 ? `Total Gain: +$${selectedDayStats.totalGain.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `Total Loss: -$${Math.abs(selectedDayStats.totalGain).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                        </p>
                      </div>
                      
                      {/* Edit Button */}
                      <button
                        type="button"
                        onClick={() => setIsEditingDate(true)}
                        className="bg-[#1b1c24] border border-[#2d2f3d] hover:bg-[#252733] text-white text-[11px] font-black uppercase tracking-wider py-1.5 px-4 rounded-xl flex items-center gap-1.5 transition-colors"
                      >
                        <Edit2 size={12} />
                        <span>Edit</span>
                      </button>
                    </div>

                    {/* Stats Cards: Single Horizontal Row (6 cards) */}
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
                      <ModalStatCard label="TRADES" value={selectedDayStats.tradesCount} />
                      <ModalStatCard label="WINRATE" value={`${selectedDayStats.winRate}%`} valueColor="text-[#22C55E]" />
                      <ModalStatCard label="GROSS PNL" value={selectedDayStats.grossPnl >= 0 ? `+$${selectedDayStats.grossPnl.toFixed(2)}` : `-$${Math.abs(selectedDayStats.grossPnl).toFixed(2)}`} valueColor={selectedDayStats.grossPnl >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'} />
                      <ModalStatCard label="WINNERS" value={selectedDayStats.winners} />
                      <ModalStatCard label="LOSERS" value={selectedDayStats.losers} />
                      <ModalStatCard label="COMMS" value={`$${selectedDayStats.comms.toFixed(2)}`} />
                    </div>

                    {/* Executions ledger table: Full Width */}
                    <div className="bg-[#14151E] rounded-xl border border-[#16171F] overflow-hidden max-h-[300px] overflow-y-auto" data-lenis-prevent>
                      <table className="w-full text-left border-collapse font-sans">
                        <thead className="bg-[#0E0F14] text-[8px] uppercase font-bold text-neutral-500 tracking-wider border-b border-[#16171F]">
                          <tr>
                            <th className="px-5 py-3">OPEN TIME</th>
                            <th className="px-5 py-3">SYMBOL</th>
                            <th className="px-5 py-3">TICKER</th>
                            <th className="px-5 py-3">SIDE</th>
                            <th className="px-5 py-3">VOLUME</th>
                            <th className="px-5 py-3 text-right">PNL</th>
                            <th className="px-5 py-3 text-right">NET ROI</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#16171F]">
                          {selectedDayStats.tradesList.length > 0 ? (
                            selectedDayStats.tradesList.map((t, idx) => (
                              <tr key={idx} className="hover:bg-neutral-900/20 transition-colors">
                                <td className="px-5 py-3 text-[10px] font-bold text-neutral-400 font-mono">{t.time}</td>
                                <td className="px-5 py-3 text-[10px] font-bold text-white">{t.symbol}</td>
                                <td className="px-5 py-3 text-[10px] font-black text-[#E50914]">{t.ticker}</td>
                                <td className="px-5 py-3">
                                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${t.side === 'LONG' || t.side === 'BUY' ? 'bg-[#3b82f6]/10 text-[#3b82f6]' : 'bg-[#f59e0b]/10 text-[#f59e0b]'}`}>
                                    {t.side}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-[10px] font-bold text-neutral-400 font-mono">{t.volume}</td>
                                <td className={`px-5 py-3 text-[10px] font-bold text-right font-mono ${t.pnl >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                                  {t.pnl >= 0 ? `+$${t.pnl.toFixed(2)}` : `-$${Math.abs(t.pnl).toFixed(2)}`}
                                </td>
                                <td className={`px-5 py-3 text-[10px] font-bold text-right font-mono ${t.roi >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                                  {t.roi >= 0 ? `+${(t.roi * 100).toFixed(2)}%` : `-${Math.abs(t.roi * 100).toFixed(2)}%`}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={7} className="px-5 py-8 text-center text-xs text-neutral-500 italic">No trades executed on this day.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Interactive Onboarding Tour */}
        <ProductTour steps={tourSteps} tourKey="reports" />

        {/* Hidden PDF Report Template Container (Off-screen) */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          
          {/* PAGE 1: Header, growth chart, main stats and long/short ratio */}
          <div id="pdf-page-1" style={{ width: '800px', height: '1130px', padding: '45px', boxSizing: 'border-box', backgroundColor: '#ffffff', color: '#000000', fontFamily: 'Arial, sans-serif', position: 'relative' }}>
            {/* Watermark Logo */}
            <img src={invertedLogoB64 || LOGO_BASE64} style={{ position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)', width: '380px', opacity: 0.08, pointerEvents: 'none', zIndex: 10 }} alt="watermark" />
            
            <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              {/* Header */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #1a1a1a', paddingBottom: '15px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                      <img src={invertedLogoB64 || LOGO_BASE64} style={{ height: '32px', width: 'auto' }} alt="logo" />
                      <h1 style={{ fontSize: '26px', fontWeight: 'bold', margin: 0, color: '#1a1a1a', letterSpacing: '-0.5px' }}>CatatCrypto Statement</h1>
                    </div>
                    <p style={{ fontSize: '11px', fontWeight: 'bold', color: '#666666', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>Official Performance Audit Log</p>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '11px', color: '#333333', lineHeight: '1.4' }}>
                    <div style={{ marginBottom: '2px' }}><strong>Account:</strong> {selectedAccount?.account_label || selectedWalletAccount?.account_label || 'All Accounts'}</div>
                    <div style={{ marginBottom: '2px' }}><strong>Platform:</strong> {selectedAccount?.cex_name?.toUpperCase() || (selectedWalletAccount ? 'On-Chain' : 'ALL')}</div>
                    <div style={{ marginBottom: '2px' }}><strong>Timeframe:</strong> Last 90 Days</div>
                    <div><strong>Generated:</strong> {new Date().toLocaleDateString('en-US', { dateStyle: 'medium' })}</div>
                  </div>
                </div>
                
                {/* Balance Chart Section */}
                <div style={{ marginTop: '25px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#1a1a1a', textTransform: 'uppercase', margin: '0 0 10px 0', letterSpacing: '0.5px' }}>Equity Growth History</h3>
                  <div style={{ border: '1px solid #e5e5e5', borderRadius: '12px', padding: '15px', backgroundColor: '#fcfcfc' }}>
                    <svg width="710" height="180" viewBox="0 0 710 180">
                      <defs>
                        <linearGradient id="chart-grad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#22C55E" stopOpacity="0.25"/>
                          <stop offset="100%" stopColor="#22C55E" stopOpacity="0.0"/>
                        </linearGradient>
                      </defs>
                      {points.length > 0 && (
                        <>
                          <path d={areaD} fill="url(#chart-grad)" />
                          <path d={pathD} fill="none" stroke="#22C55E" strokeWidth="2.5" />
                          {/* Grid lines */}
                          <line x1="0" y1="45" x2="710" y2="45" stroke="#efefef" strokeDasharray="3,3" />
                          <line x1="0" y1="90" x2="710" y2="90" stroke="#efefef" strokeDasharray="3,3" />
                          <line x1="0" y1="135" x2="710" y2="135" stroke="#efefef" strokeDasharray="3,3" />
                        </>
                      )}
                    </svg>
                  </div>
                </div>
  
                {/* Summary Metrics Section */}
                <div style={{ marginTop: '25px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#1a1a1a', textTransform: 'uppercase', margin: '0 0 10px 0', letterSpacing: '0.5px' }}>Performance Metrics Summary</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '8px 0', color: '#666666' }}>Deposit / Withdrawal:</td>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', textAlign: 'right' }}>$0.00</td>
                        <td style={{ padding: '8px 0 8px 30px', color: '#666666' }}>Profit Factor:</td>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', textAlign: 'right' }}>{profitFactorStr}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '8px 0', color: '#666666' }}>Gross Profit:</td>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#22c55e', textAlign: 'right' }}>+${grossProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '8px 0 8px 30px', color: '#666666' }}>Sharpe Ratio:</td>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', textAlign: 'right' }}>{trades.length > 0 ? '1.82' : '0.00'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '8px 0', color: '#666666' }}>Gross Loss:</td>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#ef4444', textAlign: 'right' }}>-${grossLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '8px 0 8px 30px', color: '#666666' }}>Win Rate:</td>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', textAlign: 'right' }}>{winRateStr}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '8px 0', color: '#666666' }}>Net Profit:</td>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', color: pnlPositive ? '#22c55e' : '#ef4444', textAlign: 'right' }}>{pnl}</td>
                        <td style={{ padding: '8px 0 8px 30px', color: '#666666' }}>Total Trades:</td>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', textAlign: 'right' }}>{totalTradesCount}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
  
                {/* Long / Short Positions Section */}
                <div style={{ marginTop: '25px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#1a1a1a', textTransform: 'uppercase', margin: '0 0 10px 0', letterSpacing: '0.5px' }}>Long vs Short Position Breakdown</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e5e5', backgroundColor: '#f9f9f9' }}>
                        <th style={{ padding: '8px 10px', color: '#555' }}>Position Type</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'center' }}>Total Trades</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'center' }}>Wins</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'center' }}>Losses</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'center' }}>Win Rate</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'right' }}>Net Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '10px', fontWeight: 'bold' }}>LONG (BUY)</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{longCount}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#22c55e' }}>{longWins}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#ef4444' }}>{longCount - longWins}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold' }}>{longCount > 0 ? `${Math.round((longWins / longCount) * 100)}%` : '0%'}</td>
                        <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: longPnl >= 0 ? '#22c55e' : '#ef4444' }}>
                          {longPnl >= 0 ? `+$${longPnl.toFixed(2)}` : `-$${Math.abs(longPnl).toFixed(2)}`}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '10px', fontWeight: 'bold' }}>SHORT (SELL)</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{shortCount}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#22c55e' }}>{shortWins}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#ef4444' }}>{shortCount - shortWins}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold' }}>{shortCount > 0 ? `${Math.round((shortWins / shortCount) * 100)}%` : '0%'}</td>
                        <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: shortPnl >= 0 ? '#22c55e' : '#ef4444' }}>
                          {shortPnl >= 0 ? `+$${shortPnl.toFixed(2)}` : `-$${Math.abs(shortPnl).toFixed(2)}`}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Footer */}
              <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: '10px', fontSize: '9px', color: '#999999', display: 'flex', justifyContent: 'space-between' }}>
                <span>CatatCrypto Premium Performance Audit Statement</span>
                <span>Page 1 of 3</span>
              </div>
            </div>
          </div>
  
          {/* PAGE 2: Symbols breakdown & Risk stats */}
          <div id="pdf-page-2" style={{ width: '800px', height: '1130px', padding: '45px', boxSizing: 'border-box', backgroundColor: '#ffffff', color: '#000000', fontFamily: 'Arial, sans-serif', position: 'relative' }}>
            {/* Watermark Logo */}
            <img src={invertedLogoB64 || LOGO_BASE64} style={{ position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)', width: '380px', opacity: 0.08, pointerEvents: 'none', zIndex: 10 }} alt="watermark" />
            
            <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #1a1a1a', paddingBottom: '15px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                      <img src={invertedLogoB64 || LOGO_BASE64} style={{ height: '32px', width: 'auto' }} alt="logo" />
                      <h1 style={{ fontSize: '26px', fontWeight: 'bold', margin: 0, color: '#1a1a1a', letterSpacing: '-0.5px' }}>CatatCrypto Statement</h1>
                    </div>
                    <p style={{ fontSize: '11px', fontWeight: 'bold', color: '#666666', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>Symbol Breakdown & Risk Analysis</p>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '11px', color: '#333333' }}>
                    <div><strong>Generated:</strong> {new Date().toLocaleDateString('en-US', { dateStyle: 'medium' })}</div>
                  </div>
                </div>
  
                {/* Symbols Breakdown Section */}
                <div style={{ marginTop: '25px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#1a1a1a', textTransform: 'uppercase', margin: '0 0 10px 0', letterSpacing: '0.5px' }}>Symbols & Asset Performance</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e5e5', backgroundColor: '#f9f9f9' }}>
                        <th style={{ padding: '8px 10px', color: '#555' }}>Symbol</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'center' }}>Trades</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'center' }}>Win Rate</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'center' }}>Profit Factor</th>
                        <th style={{ padding: '8px 10px', color: '#555', textAlign: 'right' }}>Net Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {symbolStats.length > 0 ? (
                        symbolStats.slice(0, 10).map((stat, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #efefef' }}>
                            <td style={{ padding: '10px', fontWeight: 'bold' }}>{stat.symbol}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{stat.total}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{stat.winRate}%</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{stat.profitFactor}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: stat.pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                              {stat.pnl >= 0 ? `+$${stat.pnl.toFixed(2)}` : `-$${Math.abs(stat.pnl).toFixed(2)}`}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>No symbol data available.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
  
                {/* Risks & Extreme Statistics Section */}
                <div style={{ marginTop: '30px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#1a1a1a', textTransform: 'uppercase', margin: '0 0 10px 0', letterSpacing: '0.5px' }}>Trading Psychology & Risk Stats</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '10px 0', color: '#666666' }}>Largest Profit Trade:</td>
                        <td style={{ padding: '10px 0', fontWeight: 'bold', color: '#22c55e', textAlign: 'right' }}>
                          {isDemoMode ? "+$12,400.00" : (biggestProfitTrade ? `+$${biggestProfitTrade.pnl_amount.toFixed(2)}` : "$0.00")}
                        </td>
                        <td style={{ padding: '10px 0 10px 30px', color: '#666666' }}>Average Winning Trade:</td>
                        <td style={{ padding: '10px 0', fontWeight: 'bold', color: '#22c55e', textAlign: 'right' }}>
                          {totalTradesCount > 0 ? `+$${(grossProfit / (trades.filter(t => (t.pnl_amount || 0) > 0).length || 1)).toFixed(2)}` : '$0.00'}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '10px 0', color: '#666666' }}>Largest Loss Trade:</td>
                        <td style={{ padding: '10px 0', fontWeight: 'bold', color: '#ef4444', textAlign: 'right' }}>
                          {isDemoMode ? "-$3,200.00" : (biggestLossTrade ? `-$${Math.abs(biggestLossTrade.pnl_amount).toFixed(2)}` : "$0.00")}
                        </td>
                        <td style={{ padding: '10px 0 10px 30px', color: '#666666' }}>Average Losing Trade:</td>
                        <td style={{ padding: '10px 0', fontWeight: 'bold', color: '#ef4444', textAlign: 'right' }}>
                          {totalTradesCount > 0 ? `-$${(grossLoss / (trades.filter(t => (t.pnl_amount || 0) < 0).length || 1)).toFixed(2)}` : '$0.00'}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #efefef' }}>
                        <td style={{ padding: '10px 0', color: '#666666' }}>Max Consecutive Wins (Count):</td>
                        <td style={{ padding: '10px 0', fontWeight: 'bold', color: '#22c55e', textAlign: 'right' }}>
                          {maxWins}
                        </td>
                        <td style={{ padding: '10px 0 10px 30px', color: '#666666' }}>Max Consecutive Losses (Count):</td>
                        <td style={{ padding: '10px 0', fontWeight: 'bold', color: '#ef4444', textAlign: 'right' }}>
                          {maxLosses}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
  
              {/* Footer */}
              <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: '10px', fontSize: '9px', color: '#999999', display: 'flex', justifyContent: 'space-between' }}>
                <span>CatatCrypto Premium Performance Audit Statement</span>
                <span>Page 2 of 3</span>
              </div>
            </div>
          </div>
  
          {/* PAGE 3: Closed Trades Log */}
          <div id="pdf-page-3" style={{ width: '800px', height: '1130px', padding: '45px', boxSizing: 'border-box', backgroundColor: '#ffffff', color: '#000000', fontFamily: 'Arial, sans-serif', position: 'relative' }}>
            {/* Watermark Logo */}
            <img src={invertedLogoB64 || LOGO_BASE64} style={{ position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)', width: '380px', opacity: 0.08, pointerEvents: 'none', zIndex: 10 }} alt="watermark" />
            
            <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              {/* Header */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #1a1a1a', paddingBottom: '15px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                      <img src={invertedLogoB64 || LOGO_BASE64} style={{ height: '32px', width: 'auto' }} alt="logo" />
                      <h1 style={{ fontSize: '26px', fontWeight: 'bold', margin: 0, color: '#1a1a1a', letterSpacing: '-0.5px' }}>CatatCrypto Statement</h1>
                    </div>
                    <p style={{ fontSize: '11px', fontWeight: 'bold', color: '#666666', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>Closed Trades Audit History</p>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '11px', color: '#333333' }}>
                    <div><strong>Generated:</strong> {new Date().toLocaleDateString('en-US', { dateStyle: 'medium' })}</div>
                  </div>
                </div>
  
                {/* Trades History Table */}
                <div style={{ marginTop: '25px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#1a1a1a', textTransform: 'uppercase', margin: '0 0 10px 0', letterSpacing: '0.5px' }}>Recent Closed Positions Logs</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e5e5', backgroundColor: '#f9f9f9' }}>
                        <th style={{ padding: '6px 8px', color: '#555' }}>Date / Time</th>
                        <th style={{ padding: '6px 8px', color: '#555' }}>Symbol</th>
                        <th style={{ padding: '6px 8px', color: '#555' }}>Side</th>
                        <th style={{ padding: '6px 8px', color: '#555', textAlign: 'right' }}>Size</th>
                        <th style={{ padding: '6px 8px', color: '#555', textAlign: 'right' }}>Entry Price</th>
                        <th style={{ padding: '6px 8px', color: '#555', textAlign: 'right' }}>Commission</th>
                        <th style={{ padding: '6px 8px', color: '#555', textAlign: 'right' }}>P&L (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trades.length > 0 ? (
                        [...trades]
                          .sort((a, b) => new Date(b.trade_date).getTime() - new Date(a.trade_date).getTime())
                          .slice(0, 32)
                          .map((t, idx) => {
                            const pnlVal = t.pnl_amount || 0;
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #efefef' }}>
                                <td style={{ padding: '8px', fontStyle: 'normal' }}>
                                  {new Date(t.trade_date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                                </td>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{t.pairs}</td>
                                <td style={{ padding: '8px' }}>
                                  <span style={{ fontWeight: 'bold', color: t.direction === 'LONG' || t.direction === 'BUY' ? '#3b82f6' : '#f59e0b' }}>
                                    {t.direction || 'BUY'}
                                  </span>
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{t.position_size?.toFixed(4) || '—'}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>${t.entry_price?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '—'}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>${t.commission?.toFixed(2) || '0.00'}</td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: pnlVal >= 0 ? '#22c55e' : '#ef4444' }}>
                                  {pnlVal >= 0 ? `+$${pnlVal.toFixed(2)}` : `-$${Math.abs(pnlVal).toFixed(2)}`}
                                </td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>No trade history log available.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {trades.length > 32 && (
                    <div style={{ textAlign: 'center', color: '#888', fontSize: '9px', marginTop: '12px', fontStyle: 'italic' }}>
                      * Showing the 32 most recent trades. Complete audit history log is available inside your CatatCrypto web dashboard.
                    </div>
                  )}
                </div>
              </div>
  
              {/* Footer */}
              <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: '10px', fontSize: '9px', color: '#999999', display: 'flex', justifyContent: 'space-between' }}>
                <span>CatatCrypto Premium Performance Audit Statement</span>
                <span>Page 3 of 3</span>
              </div>
            </div>
          </div>
  
        </div>

        {/* Interactive Onboarding Tour */}
        <ProductTour steps={tourSteps} tourKey="reports" />
      </main>
    </div>
  );
};

// --- Sub Components ---

const ErrorBadge = ({ message }: { message?: string }) => {
  if (!message) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-1.5 bg-red-950/20 border border-red-900/30 rounded-lg px-2.5 py-2 mb-2"
    >
      <AlertCircle size={12} className="text-[#E50914] flex-shrink-0" />
      <span className="text-[9px] font-bold text-[#E50914] uppercase tracking-wide leading-none">{message}</span>
    </motion.div>
  );
};

const MiniStatCard = ({ label, value, subValue, positive = false }: any) => (
  <div className="bg-[#0B0C10] border border-[#16171F] rounded-2xl p-3 md:p-6 hover:border-[#1E1F29] hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-300 relative group overflow-hidden select-none font-sans">
    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/[0.01] to-transparent pointer-events-none" />
    <p className="text-[10px] uppercase font-semibold text-neutral-500 tracking-wider mb-2.5">{label}</p>
    <h3 className={`text-base md:text-2xl font-bold tracking-tight ${positive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} style={{ fontVariantNumeric: 'tabular-nums', ...getDynamicFontSizeStyle(value, 16, 24) }}>{value}</h3>
    {subValue && <p className="text-xs text-neutral-400 font-medium mt-1.5">{subValue}</p>}
  </div>
);

const ReportCard = ({ title, icon, children }: any) => (
  <div className="bg-[#0B0C10] border border-[#16171F] rounded-3xl p-8 shadow-[0_10px_35px_rgba(0,0,0,0.3)] hover:border-[#1E1F29] transition-colors duration-300 font-sans">
     <div className="flex items-center gap-2.5 mb-2 select-none">
        {icon}
        <h3 className="text-base font-bold text-white tracking-tight">{title}</h3>
     </div>
     {children}
  </div>
);

const DayBar = ({ height, day, positive = false, inactive = false }: any) => (
  <div className="flex-1 flex flex-col items-center gap-3.5 group h-full select-none">
     <div className="w-full flex-1 flex flex-col justify-end">
        <motion.div 
          initial={{ height: 0 }}
          animate={{ height }}
          className={`w-full rounded-lg transition-all duration-300 ${
            inactive 
              ? 'bg-[#181922]' 
              : positive 
                ? 'bg-gradient-to-t from-[#22C55E]/30 to-[#22C55E] shadow-[0_0_8px_rgba(34,197,94,0.2)]' 
                : 'bg-gradient-to-t from-[#EF4444]/30 to-[#EF4444] shadow-[0_0_8px_rgba(239,68,68,0.2)]'
          } hover:brightness-110 cursor-pointer`} 
        />
     </div>
     <span className="text-xs font-semibold text-neutral-500 font-mono">{day}</span>
  </div>
);

const SetupRow = ({ label, value, percent, barColor = 'bg-[#E50914]' }: any) => (
  <div className="space-y-2 py-1 font-sans">
     <div className="flex justify-between text-xs font-bold select-none">
        <span className="text-neutral-400">{label}</span>
        <span className="text-white">{value}</span>
     </div>
     <div className="h-1.5 w-full bg-[#181922] rounded-full overflow-hidden">
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: percent }}
          className={`h-full ${barColor}`} 
        />
     </div>
  </div>
);

const IndicatorRow = ({ code, label, winRate, percent }: any) => (
  <div className="flex items-center gap-4 py-1.5 font-sans">
     <div className="w-10 h-10 bg-[#0E0F14] rounded-xl flex items-center justify-center text-[10px] font-black text-neutral-400 border border-[#16171F] shrink-0 font-sans select-none">{code}</div>
     <div className="flex-1 space-y-1.5">
        <div className="flex justify-between text-xs font-bold">
           <span className="text-white">{label}</span>
           <span className="text-neutral-500 font-semibold">{winRate}</span>
        </div>
        <div className="h-1.5 w-full bg-[#181922] rounded-full overflow-hidden">
           <motion.div 
             initial={{ width: 0 }}
             animate={{ width: percent }}
             className="h-full bg-[#E50914]" 
           />
        </div>
     </div>
  </div>
);

const HoldBubble = ({ label, subLabel, duration, posHeight, negHeight }: any) => {
  const posSize = posHeight ? parseInt(posHeight) : 0;
  const negSize = negHeight ? parseInt(negHeight) : 0;

  return (
    <div className="flex flex-col items-center justify-between h-full select-none font-sans" style={{ minWidth: 0, flex: 1 }}>
      {/* Upper half: positive bubble anchored to bottom of upper half */}
      <div className="flex-1 flex flex-col items-center justify-end pb-1">
        {label ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="bg-[#22C55E]/10 border border-[#22C55E]/20 rounded-full flex items-center justify-center font-extrabold text-[#22C55E] shadow-[0_0_20px_rgba(34,197,94,0.08)] hover:scale-110 transition-transform cursor-pointer"
            style={{
              width: `min(${posSize || 40}px, 18vw)`,
              height: `min(${posSize || 40}px, 18vw)`,
              fontSize: posSize > 60 ? 12 : 9
            }}
          >
            {label}
          </motion.div>
        ) : (
          <div style={{ width: 'min(40px, 18vw)', height: 'min(40px, 18vw)' }} />
        )}
      </div>

      {/* Center line marker */}
      <div className="w-full h-px bg-[#181922] shrink-0" />

      {/* Lower half: negative bubble anchored to top of lower half */}
      <div className="flex-1 flex flex-col items-center justify-start pt-1">
        {subLabel ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-full flex items-center justify-center font-extrabold text-[#EF4444] shadow-[0_0_20px_rgba(239,68,68,0.08)] hover:scale-110 transition-transform cursor-pointer"
            style={{
              width: `min(${negSize || 40}px, 18vw)`,
              height: `min(${negSize || 40}px, 18vw)`,
              fontSize: negSize > 60 ? 12 : 9
            }}
          >
            {subLabel}
          </motion.div>
        ) : (
          <div style={{ width: 'min(40px, 18vw)', height: 'min(40px, 18vw)' }} />
        )}
      </div>

      {/* Duration label */}
      <span className="text-[8px] md:text-xs font-semibold text-neutral-500 font-mono shrink-0 mt-1 truncate max-w-full text-center">{duration}</span>
    </div>
  );
};

const CalendarCell = ({ date, pnl, trades, positive, disabled, active, onClick }: any) => (
  <div
    onClick={disabled ? undefined : onClick}
    onTouchEnd={disabled ? undefined : (e) => { e.preventDefault(); onClick?.(); }}
    className={`p-1 md:p-4 min-h-[48px] md:min-h-[110px] relative transition-all flex flex-col justify-between font-sans touch-manipulation ${
      disabled 
        ? 'opacity-10 pointer-events-none' 
        : 'hover:bg-neutral-800/10 cursor-pointer bg-[#0B0C10] active:bg-neutral-800/20'
    } ${active ? 'ring-1 ring-[#E50914] ring-inset bg-red-950/10' : ''}`}
  >
     <span className={`text-[9px] md:text-xs font-bold ${active ? 'text-white' : 'text-neutral-500 font-mono'}`}>{date}</span>
     {pnl && (
       <div className="mt-auto space-y-1 select-none">
         <p className={`text-[8px] md:text-xs font-extrabold tracking-tight font-sans ${positive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>{pnl}</p>
         <p className="hidden md:block text-[8px] font-black uppercase text-neutral-500 tracking-widest font-sans">{trades}</p>
       </div>
     )}
  </div>
);

const ModalStatCard = ({ label, value, valueColor = 'text-white' }: any) => (
  <div className="bg-[#0E0F14] p-4 rounded-xl border border-[#16171F] font-sans">
     <p className="text-[8px] font-black uppercase text-neutral-500 tracking-widest mb-1.5">{label}</p>
     <p className={`text-sm font-bold font-mono ${valueColor}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
  </div>
);

export default ReportsPage;
